<script setup lang="ts">
import type { NuxtError } from '#app';

interface Props {
  error: NuxtError;
}

const props = defineProps<Props>();

useHead({
  title: props.error.message || 'Error',
  meta: [
    {
      name: 'description',
      content: props.error.message || 'An error occurred',
    },
  ],
});

const localePath = useLocalePath();

const errorImage = computed<string>(() => {
  switch (props.error.statusCode) {
    case 404:
      return '/errors/error-404.svg';
    case 403:
      return '/errors/error-403.svg';
    default:
      return '/errors/error-404.svg';
  }
});

const handleClearError = (): void => {
  clearError({
    redirect: localePath('/')
  });
};

/**
 * Everything that is not a 404 or a 403 is treated as temporary, so the way
 * out is to ask for the same page again rather than to walk the visitor back
 * to the home page. A full reload re-runs the middleware that failed.
 */
const handleRetry = (): void => {
  reloadNuxtApp({ persistState: false });
};
</script>

<template>
  <ErrorViews404
    v-if="error.statusCode === 404"
    :image-path="errorImage"
    @handle-clear-error="handleClearError" />

  <ErrorViews403
    v-else-if="error.statusCode === 403"
    :image-path="errorImage"
    @handle-clear-error="handleClearError" />

  <ErrorViewsUnavailable
    v-else
    @handle-retry="handleRetry"
    @handle-clear-error="handleClearError" />
</template>
